import { createReducer } from '@reduxjs/toolkit';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { AreaOfGuidance } from '@packages/shared/core/quiz/entities';
import { randomNumber } from '@packages/shared/core/common/utils/random';
import {
  signInByTokenReset,
  signInFailed,
  signInSucceed,
  signInPending,
  updateEmailFailed,
  updateEmailSucceed,
  updateEmailPending,
  updateUserPreferences,
  signInByTokenPending,
  signInByTokenSucceed,
  signInByTokenFailed,
  signUpViaGooglePending,
  signUpViaGoogleFailed,
  signUpViaGoogleSucceed,
  signUpViaFacebookPending,
  signUpViaFacebookSucceed,
  signUpViaFacebookFailed,
  signUpViaQuizPending,
  signUpViaQuizFailed,
  signUpViaQuizSucceed,
} from './actions';
import { UserState } from './state';

export const userInitialState: UserState = {
  signInByTokenStatus: LoadingStatuses.IDLE,
  gender: '',
  goal: '',
  date: '',
  decisions: '',
  place: ['', ''],
  location: null,
  time: null,
  name: '',
  partnerGender: '',
  partnerDate: '',
  partnerPlace: ['', ''],
  partnerLocation: null,
  partnerTime: null,
  email: '',
  id: '',
  signUpError: '',
  whatToExpect: '',
  relationshipStatus: '',
  signInError: '',
  password: '',
  headOrHeart: '',
  howConfident: '',
  worriesImpact: '',
  whatEmotion: '',
  psychicComfortable: [],
  guidanceArea: [],
  signUpByGoogleStatus: LoadingStatuses.IDLE,
  signUpByFacebookStatus: LoadingStatuses.IDLE,
  registrationStatus: LoadingStatuses.IDLE,
  signInLoadingStatus: LoadingStatuses.IDLE,
  emailUpdateStatus: LoadingStatuses.IDLE,
  emailAlreadyExists: false,
  topicToDiscuss: AreaOfGuidance.FUTURE_TELLING,
  typeOfReading: {
    text: '',
    value: '',
  },
  funnel: '',
  randomExpertSpecializationsIndices: [
    [randomNumber(0, 3), randomNumber(4, 5)],
    [randomNumber(4, 5), randomNumber(0, 3)],
    [randomNumber(0, 1), randomNumber(2, 5)],
    [randomNumber(0, 2), randomNumber(3, 5)],
    [randomNumber(3, 5), randomNumber(0, 2)],
    [randomNumber(2, 5), randomNumber(0, 1)],
    [randomNumber(0, 3), randomNumber(4, 5)],
  ],
};

export const userReducer = createReducer(userInitialState, (builder) => {
  builder
    .addCase(updateUserPreferences, (state, { payload }) => ({
      ...state,
      [payload.preferenceName]: payload.preferenceValue,
    }))
    .addCase(signInByTokenPending, (state) => {
      state.signInByTokenStatus = LoadingStatuses.PENDING;
    })
    .addCase(signInByTokenFailed, (state) => {
      state.signInByTokenStatus = LoadingStatuses.FAILED;
    })
    .addCase(signInByTokenSucceed, (state, { payload }) => {
      if (!payload) return;

      state.signInByTokenStatus = LoadingStatuses.FULFILLED;
      state.id = payload;
    })

    .addCase(signInByTokenReset, (state) => {
      state.signInByTokenStatus = LoadingStatuses.IDLE;
    })
    .addCase(signUpViaGooglePending, (state) => ({
      ...state,
      signUpError: '',
      registrationStatus: LoadingStatuses.IDLE,
      signUpByGoogleStatus: LoadingStatuses.PENDING,
    }))
    .addCase(signUpViaGoogleFailed, (state, action) => ({
      ...state,
      signUpError: action.payload as string,
      signUpByGoogleStatus: LoadingStatuses.FAILED,
    }))
    .addCase(signUpViaGoogleSucceed, (state, { payload }) => ({
      ...state,
      email: payload.email,
      id: payload.id,
      signUpByGoogleStatus: LoadingStatuses.FULFILLED,
      registrationStatus: LoadingStatuses.FULFILLED,
    }))

    .addCase(signUpViaFacebookPending, (state) => ({
      ...state,
      signUpError: '',
      registrationStatus: LoadingStatuses.IDLE,
      signUpByFacebookStatus: LoadingStatuses.PENDING,
    }))
    .addCase(signUpViaFacebookFailed, (state, action) => ({
      ...state,
      signUpError: action.payload as string,
      signUpByFacebookStatus: LoadingStatuses.FAILED,
    }))
    .addCase(signUpViaFacebookSucceed, (state, { payload }) => ({
      ...state,
      email: payload.email || '',
      id: payload.id,
      signUpByFacebookStatus: LoadingStatuses.FULFILLED,
      registrationStatus: LoadingStatuses.FULFILLED,
    }))

    .addCase(signUpViaQuizPending, (state) => ({
      ...state,
      signUpError: '',
      signUpByGoogleStatus: LoadingStatuses.IDLE,
      registrationStatus: LoadingStatuses.PENDING,
    }))
    .addCase(signUpViaQuizFailed, (state, action) => {
      return {
        ...state,
        signUpError: action.payload as string,
        registrationStatus: LoadingStatuses.FAILED,
      };
    })
    .addCase(signUpViaQuizSucceed, (state, { payload }) => ({
      ...state,
      email: payload.email,
      id: payload.id,
      signUpError: '',
      registrationStatus: LoadingStatuses.FULFILLED,
    }))
    .addCase(signInPending, (state) => {
      state.signInLoadingStatus = LoadingStatuses.PENDING;
      state.signInError = '';
    })
    .addCase(signInSucceed, (state) => {
      state.signInLoadingStatus = LoadingStatuses.FULFILLED;
      state.signInError = '';
    })
    .addCase(signInFailed, (state, { payload }) => {
      state.signInLoadingStatus = LoadingStatuses.FAILED;
      state.signInError = payload as string;
    })
    .addCase(updateEmailPending, (state) => {
      state.emailUpdateStatus = LoadingStatuses.PENDING;
    })
    .addCase(updateEmailFailed, (state) => {
      state.emailUpdateStatus = LoadingStatuses.FAILED;
    })
    .addCase(updateEmailSucceed, (state, { payload }) => {
      state.email = payload.email;
      state.emailUpdateStatus = LoadingStatuses.FULFILLED;
    });
});
